import type { CostByKind, CostDay, CostMonthTotal, CostResource } from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import { Coins, Download } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { recentMonths } from "../../api/costs.js";
import { TimeChart, type ChartSeries } from "../../ui/chart.js";
import { dateTime, money } from "../../ui/format.js";
import { FieldHint } from "../../ui/help.js";
import { EmptyState, Section } from "../../ui/layout.js";
import { Alert, Badge, Card, Select } from "../../ui/primitives.js";
import { DataTable, type Column } from "../../ui/table.js";

/*
 * Parts of both cost pages (docs/costs.md): the estimate notice, the month
 * picker and export, the tiles, the breakdown by kind, the charts by day and
 * by month, and the table of resources.
 */

/** Formats an amount of the view's currency in the person's language. */
export function useMoney(currency: string | undefined): (amount: string | number) => string {
  const { i18n } = useTranslation();
  return (amount) => money(String(amount), currency ?? "EUR", i18n.language);
}

/** Said on every cost screen: these numbers are our estimate, net of VAT. */
export function EstimateNotice({ meteredAt }: { meteredAt: string | null | undefined }) {
  const { t, i18n } = useTranslation();
  return (
    <Alert tone="neutral">
      <p>{t("costs.estimate")}</p>
      <p className="mt-1 text-xs opacity-80">
        {meteredAt
          ? t("costs.meteredAt", { time: dateTime(meteredAt, i18n.language) })
          : t("costs.notMetered")}
      </p>
    </Alert>
  );
}

/** Month picker (last 24 months) and the CSV download of that month. */
export function MonthTools({
  month,
  onMonth,
  exportUrl,
}: {
  month: string;
  onMonth: (month: string) => void;
  exportUrl: string;
}) {
  const { t, i18n } = useTranslation();
  const months = useMemo(() => recentMonths(), []);
  const name = (key: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${key}-01T00:00:00Z`));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select label={t("costs.month")} value={month} onChange={(e) => onMonth(e.target.value)}>
        {months.map((m) => (
          <option key={m} value={m}>
            {name(m)}
          </option>
        ))}
      </Select>
      <a
        href={exportUrl}
        download
        className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
      >
        <Download className="size-4" aria-hidden />
        {t("costs.export")}
      </a>
    </div>
  );
}

export function Tile({
  label,
  value,
  hint,
  children,
}: {
  label: string;
  value: string;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-xs text-muted">
        {label}
        {hint && <FieldHint text={hint} topic="costs" />}
      </span>
      <span className="text-2xl font-semibold tabular-nums">{value}</span>
      {children}
    </Card>
  );
}

/** Month to date (the whole month for past ones), forecast and last month. */
export function CostTiles({
  monthToDate,
  forecast,
  lastMonth,
  currency,
  current,
  children,
}: {
  monthToDate: string;
  forecast: string | null;
  lastMonth: string;
  currency: string;
  current: boolean;
  /** More tiles (the budget, stage 3). */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const fmt = useMoney(currency);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Tile
        label={current ? t("costs.monthToDate") : t("costs.monthTotal")}
        value={fmt(monthToDate)}
      />
      {current && forecast !== null && (
        <Tile label={t("costs.forecast")} value={fmt(forecast)} hint={t("costs.forecastHint")} />
      )}
      <Tile label={t("costs.lastMonth")} value={fmt(lastMonth)} />
      {children}
    </div>
  );
}

/** Each kind's share of the month as a bar. */
export function KindBreakdown({ items, currency }: { items: CostByKind[]; currency: string }) {
  const { t } = useTranslation();
  const fmt = useMoney(currency);
  const top = Math.max(...items.map((i) => Number(i.amount)), 0);
  return (
    <Section title={t("costs.byKind")}>
      <Card>
        {items.length === 0 ? (
          <p className="text-sm text-muted">{t("costs.empty")}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((item) => (
              <li key={item.kind} className="flex flex-col gap-1 text-sm">
                <span className="flex justify-between gap-3">
                  <span>{t(`costs.kinds.${item.kind}`)}</span>
                  <span className="tabular-nums">{fmt(item.amount)}</span>
                </span>
                <span className="h-1.5 overflow-hidden rounded-full bg-surface-2" aria-hidden>
                  <span
                    className="block h-full rounded-full bg-accent"
                    style={{ width: `${top > 0 ? (Number(item.amount) / top) * 100 : 0}%` }}
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Section>
  );
}

/** The month's most expensive things, as a short list. */
export function TopList({
  rows,
  currency,
  withProject = false,
}: {
  rows: CostResource[];
  currency: string;
  withProject?: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useMoney(currency);
  return (
    <Section title={t("costs.top")}>
      <Card className="py-1">
        {rows.length === 0 ? (
          <p className="py-3 text-sm text-muted">{t("costs.empty")}</p>
        ) : (
          <ol className="divide-y divide-line">
            {rows.map((r) => (
              <li
                key={`${r.projectId}:${r.kind}:${r.providerId}`}
                className="flex items-start justify-between gap-3 py-2.5 text-sm"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium" title={r.name}>
                      {r.name}
                    </span>
                    {r.deleted && <Badge>{t("costs.deleted")}</Badge>}
                  </span>
                  <span className="text-xs text-muted">
                    {t(`costs.kinds.${r.kind}`)}
                    {withProject && ` · ${r.projectName}`}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums">{fmt(r.amount)}</span>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </Section>
  );
}

const seconds = (iso: string) => Math.floor(new Date(iso).getTime() / 1000);

/** The month's trend by day and the last 24 months. */
export function CostCharts({
  days,
  months,
  currency,
}: {
  days: CostDay[];
  months: CostMonthTotal[];
  currency: string;
}) {
  const { t, i18n } = useTranslation();
  const fmt = useMoney(currency);
  const daily: ChartSeries[] = useMemo(
    () => [
      {
        label: t("costs.dayAmount"),
        points: days.map((d) => [seconds(`${d.date}T00:00:00Z`), Number(d.amount)]),
      },
    ],
    [days, t],
  );
  const monthly: ChartSeries[] = useMemo(
    () => [
      {
        label: t("costs.monthAmount"),
        points: months.map((m) => [seconds(`${m.month}-01T00:00:00Z`), Number(m.amount)]),
      },
    ],
    [months, t],
  );
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <TimeChart
        title={t("costs.byDay")}
        series={daily}
        format={(v) => fmt(v)}
        locale={i18n.language}
        empty={t("costs.chartEmpty")}
      />
      <TimeChart
        title={t("costs.byMonth")}
        series={monthly}
        format={(v) => fmt(v)}
        locale={i18n.language}
        empty={t("costs.chartEmpty")}
      />
    </div>
  );
}

/** Resources of the month, most expensive first; deleted ones stay, marked. */
export function ResourceTable({
  title,
  rows,
  currency,
  withProject = false,
  appName = (packageId) => packageId,
}: {
  title: string;
  rows: CostResource[];
  currency: string;
  withProject?: boolean;
  /** The name of the app whose managed appliance a line is (docs/packages.md). */
  appName?: (packageId: string) => string;
}) {
  const { t } = useTranslation();
  const fmt = useMoney(currency);
  const columns: Column<CostResource>[] = [
    {
      key: "name",
      header: t("costs.resource"),
      primary: true,
      sort: (r) => r.name.toLowerCase(),
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{r.name}</span>
          {r.deleted && <Badge>{t("costs.deleted")}</Badge>}
          {r.packageId && <Badge>{t("costs.appliance", { name: appName(r.packageId) })}</Badge>}
        </span>
      ),
    },
    {
      key: "kind",
      header: t("costs.kind"),
      sort: (r) => r.kind,
      cell: (r) => t(`costs.kinds.${r.kind}`),
    },
    ...(withProject
      ? [
          {
            key: "project",
            header: t("costs.project"),
            sort: (r: CostResource) => r.projectName.toLowerCase(),
            cell: (r: CostResource) => (
              <Link
                to="/projects/$projectId/costs"
                params={{ projectId: r.projectId }}
                className="text-accent hover:underline"
              >
                {r.projectName}
              </Link>
            ),
          },
        ]
      : []),
    {
      key: "amount",
      header: t("costs.amount"),
      align: "right",
      sort: (r) => Number(r.amount),
      cell: (r) => fmt(r.amount),
    },
  ];
  return (
    <Section title={title}>
      <DataTable
        label={title}
        rows={rows}
        rowKey={(r) => `${r.projectId}:${r.kind}:${r.providerId}`}
        columns={columns}
        initialSort={{ key: "amount", dir: "desc" }}
        empty={
          <EmptyState
            icon={<Coins className="size-6" aria-hidden />}
            title={t("costs.empty")}
            help="costs"
          >
            {t("costs.emptyLead")}
          </EmptyState>
        }
      />
    </Section>
  );
}
